import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const cls = status === "new" ? "new" : status === "read" ? "read" : "archived";
  const label = status === "new" ? "Neu" : status === "read" ? "Gelesen" : "Archiviert";
  return <span className={`badge ${cls}`}>{label}</span>;
}

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const nav = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!checking && (!user || user.role !== "admin")) nav("/admin/login", { replace: true });
  }, [user, checking, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([api.get("/admin/contacts"), api.get("/admin/project-requests")]);
      setContacts(c.data);
      setProjects(p.data);
    } catch (e) {
      toast.error("Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  const markStatus = async (type, id, status) => {
    const path = type === "contact" ? "contacts" : "project-requests";
    try {
      await api.patch(`/admin/${path}/${id}`, { status });
      toast.success("Status aktualisiert");
      load();
    } catch {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const del = async (type, id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    const path = type === "contact" ? "contacts" : "project-requests";
    try {
      await api.delete(`/admin/${path}/${id}`);
      toast.success("Gelöscht");
      load();
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const downloadFile = async (pid, filename) => {
    try {
      const res = await api.get(`/admin/project-requests/${pid}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "datei";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Datei-Download fehlgeschlagen");
    }
  };

  if (checking) return <div className="admin-shell p-10">Lade...</div>;
  if (!user || user.role !== "admin") return null;

  return (
    <div className="admin-shell">
      <div className="admin-header">
        <div className="container flex items-center justify-between">
          <div>
            <div className="text-xl font-light tracking-[.14em] text-neutral-900">LET'S BAU</div>
            <div className="text-xs uppercase tracking-[.18em] text-neutral-500">Admin Dashboard</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-neutral-500">{user.email}</span>
            <button className="lbtn" onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout-btn">
              Abmelden
            </button>
          </div>
        </div>
      </div>

      <div className="container py-8">
        <Tabs defaultValue="contacts">
          <TabsList className="bg-white border border-neutral-200">
            <TabsTrigger value="contacts" data-testid="tab-contacts">Kontaktanfragen ({contacts.length})</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projektanfragen ({projects.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="mt-6">
            {loading ? (
              <div className="text-neutral-500 p-6">Lade...</div>
            ) : contacts.length === 0 ? (
              <div className="text-neutral-500 p-6">Keine Kontaktanfragen vorhanden.</div>
            ) : (
              <div className="overflow-x-auto border border-neutral-200 rounded-2xl">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Name</th>
                      <th>Kontakt</th>
                      <th>Nachricht</th>
                      <th>Status</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                        <td className="text-neutral-500 whitespace-nowrap">{new Date(c.created_at).toLocaleString("de-CH")}</td>
                        <td className="text-neutral-900">{c.name}</td>
                        <td className="text-neutral-700">
                          <div>{c.email}</div>
                          {c.phone && <div className="text-xs text-neutral-500">{c.phone}</div>}
                        </td>
                        <td className="text-neutral-700 max-w-md"><div className="whitespace-pre-wrap">{c.message}</div></td>
                        <td><StatusBadge status={c.status} /></td>
                        <td>
                          <div className="flex gap-2 flex-wrap">
                            {c.status !== "read" && (
                              <button className="text-xs text-neutral-600 hover:text-neutral-900" onClick={() => markStatus("contact", c.id, "read")}>Gelesen</button>
                            )}
                            {c.status !== "archived" && (
                              <button className="text-xs text-neutral-600 hover:text-neutral-900" onClick={() => markStatus("contact", c.id, "archived")}>Archiv</button>
                            )}
                            <button className="text-xs text-red-400 hover:text-red-300" onClick={() => del("contact", c.id)}>Löschen</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="projects" className="mt-6">
            {loading ? (
              <div className="text-neutral-500 p-6">Lade...</div>
            ) : projects.length === 0 ? (
              <div className="text-neutral-500 p-6">Keine Projektanfragen vorhanden.</div>
            ) : (
              <div className="overflow-x-auto border border-neutral-200 rounded-2xl">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Name</th>
                      <th>Kontakt</th>
                      <th>Leistung</th>
                      <th>Beschreibung</th>
                      <th>Datei</th>
                      <th>Status</th>
                      <th>Aktion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => (
                      <tr key={p.id} data-testid={`project-row-${p.id}`}>
                        <td className="text-neutral-500 whitespace-nowrap">{new Date(p.created_at).toLocaleString("de-CH")}</td>
                        <td className="text-neutral-900">{p.name}</td>
                        <td className="text-neutral-700">
                          <div>{p.email}</div>
                          {p.phone && <div className="text-xs text-neutral-500">{p.phone}</div>}
                        </td>
                        <td className="text-neutral-700 whitespace-nowrap">{p.service_type}</td>
                        <td className="text-neutral-700 max-w-md"><div className="whitespace-pre-wrap">{p.description}</div></td>
                        <td>
                          {p.file_name ? (
                            <button className="text-xs text-neutral-900 underline" onClick={() => downloadFile(p.id, p.file_name)}>
                              {p.file_name}
                            </button>
                          ) : (
                            <span className="text-neutral-600 text-xs">—</span>
                          )}
                        </td>
                        <td><StatusBadge status={p.status} /></td>
                        <td>
                          <div className="flex gap-2 flex-wrap">
                            {p.status !== "read" && (
                              <button className="text-xs text-neutral-600 hover:text-neutral-900" onClick={() => markStatus("project", p.id, "read")}>Gelesen</button>
                            )}
                            {p.status !== "archived" && (
                              <button className="text-xs text-neutral-600 hover:text-neutral-900" onClick={() => markStatus("project", p.id, "archived")}>Archiv</button>
                            )}
                            <button className="text-xs text-red-400 hover:text-red-300" onClick={() => del("project", p.id)}>Löschen</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
